import on from "../util/elements/on";
import destroys from "../util/elements/destroys";
import Reactive from "../util/reactive/reactive";

const Form = new Reactive();

const moneyFormat = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
});

export const price = new Reactive({ min: 250 });

const savingMsg = '<p><small class="saving-animation">Saving</small></p>';
const minimumMsg = (amount = price.state.min) =>
  amount === 60
    ? `<p><small class="">Delivery orders have a minimum of $${amount}</small></p>`
    : `<p><small class="">Online orders have a minimum of $${amount}</small></p>`;

export default function widgetPreOrderForm(Site, $) {
  Site.widget("pre-order-form", {
    async _create() {
      destroys(this);
      /**
       * Will give the api 1200 ms to respond. After that it runs the first function
       * which covers the widget to show that it is currently loading.
       * After if resolves it runs the second function which reverts the indicator
       *  */
      const cart = await onLongWait(
        Site.callAPI("woocommerce/get-cart"),
        1200,
        () => this.element.toggleClass("long-load"),
        () => this.element.toggleClass("long-load loaded")
      );

      this.productInputs = this.element.find("#product-select .table [name]");
      this.total = this.element.find(".total-amount");
      this.button = this.element.find("a.button");
      this.messages = this.element.find(".product-select-message");

      // So we dont have to query the dom all the time
      this.elCache = {};

      /**
       * Every time an input changes we create an object of all the inputs
       * { [el1.name]: el1.value, [el2.name]: el2.value, ...etc }
       * This then becomes the value of Form.state
       */
      const onProductChange = on(this.productInputs, "input", (e) => {
        const el = $(e.target);
        dirty(el);
        removeError(el);
        Form.setState({ products: this.serializeForm(this.productInputs) });
      });
      this.updateCart = this.submitCart();

      /**
       * Every time a user meta input changes
       *
       */

      /**
       * Every time the form changes we
       */
      const onFormProductsChange = Form.changed("products", () =>
        this.updateFormView()
      );
      price.changed("min", () => {
        this.updateFormView(false);
      });
      const onUpdateCart = Form.changed("products", this.updateCart);
      const onFormMessages = Form.changed("messages", () => {
        this.messages.empty();
        const messages = Form.state.messages;

        Object.entries(messages).forEach(([msg, enabled]) => {
          if (enabled) {
            this.messages.append(msg);
          }
        });

        this.messages.toggleClass(
          "empty",
          !Object.values(messages).some(Boolean)
        );
        this.button.toggleClass(
          "disabled",
          messages[savingMsg] || messages[minimumMsg()]
        );
      });

      // Set intiial state
      Form.setState({
        products: cart
          ? this.serializeFormFromWC(cart)
          : this.serializeForm(this.productInputs),
        messages: {},
      });

      // Clean up
      this.destroys(
        onProductChange,
        onFormProductsChange,
        onUpdateCart,
        Form.destroy,
        onFormMessages
      );
    },
    updateFormView(loading = true) {
      const messages = {
        ...Form.state.messages,
        [savingMsg]: loading,
      };

      const entries = Object.entries(Form.state.products);

      entries.forEach(([name, value]) => {
        this.getEl(name).val(value);
      });

      this.totalPrice = this.getTotal() / 100;
      this.total.text(moneyFormat.format(this.totalPrice));

      // Add loading message
      price.setState({
        min: 250,
      });

      // messages[minimumMsg(80)] = price.state.min === 80 && this.totalPrice < 80
      messages[minimumMsg(250)] =
        price.state.min === 250 && this.totalPrice < 250;

      // Add loading message
      Form.setState({ messages });
    },
    serializeForm($inputs) {
      let result = {};

      $inputs.not(`[type="radio"]`).each((i, el) => {
        result[$(el).attr("name")] = $(el).val();
      });

      $inputs.filter(`[type="radio"]:checked`).each((i, el) => {
        result[$(el).attr("name")] = $(el).val();
      });

      return result;
    },
    // Serialize result from Site.callAPI(getCart)
    serializeFormFromWC(cart) {
      const items = Object.values(cart.data);

      const r = items.reduce((result, item) => {
        result["amount-" + item.product_id] = Number(item.quantity);
        return result;
      }, {});

      return r;
    },
    getTotal() {
      // Returns the cost in cents (To avoid rounding errors)
      // https://frontstuff.io/how-to-handle-monetary-values-in-javascript

      const entries = Object.entries(Form.state.products);

      const total = entries.reduce((total, [name, amount]) => {
        const numberOf = Number(amount);
        const el = this.getEl(name);
        const data = el.data();

        if (isNaN(total + data.price * numberOf)) {
          return total;
        }
        return total + data.price * numberOf;
      }, 0);

      return total;
    },
    getEl(name) {
      if (this.elCache[name]) return this.elCache[name];
      else {
        const el = this.element.find(`[name="${name}"]`);
        this.elCache[name] = el;
        return el;
      }
    },
    submitCart() {
      const shippingWidget = Site.findWidget("delivery-options");

      this.apiCall = false;
      let debounce = 1500;
      let timeout;

      const onUpdate = (data) => {
        // If it is less than minimum price ignore
        if (this.totalPrice < price.state.min) {
          Form.setState({
            messages: { ...Form.state.messages, [savingMsg]: false },
          });
        }

        if (data.isInitialChange) {
          Form.setState({
            messages: { ...Form.state.messages, [savingMsg]: false },
          });
          return;
        }
        // Will only run 1000ms after client stops spamming the button
        clearTimeout(timeout);
        timeout = setTimeout(submit, debounce);
      };

      const submit = async (data) => {
        // Wait for previous call to end
        if (this.apiCall) {
          await this.apiCall;
        }

        this.apiCall = Site.callAPI("woocommerce/set-products", {
          ...Form.state,
          shipping: this.options.deliveryActive
            ? shippingWidget.value
            : this.options.defaultDeliveryOption,
          products: Object.keys(Form.state.products).map((key) => {
            return {
              id: Number(key.replace("amount-", "")),
              amount: Form.state.products[key],
            };
          }),
        });

        const response = await this.apiCall;
        Form.setState({
          messages: { ...Form.state.messages, [savingMsg]: false },
        });

        this.apiCall = false;

        if (response.data.errors) {
          response.data.errors.forEach((err) => {
            const el = this.element.find(`.field--${err.field}`);
            const dirty = el.attr("data-dirty");

            // Only show error if the input has been used
            if (dirty) {
              const errorEl = el.find(".error");
              errorEl.text(err.msg);
              errorEl.removeClass("hidden");
            }
          });
        }
      };

      return onUpdate;
    },
  });
}

function wait(time) {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
}

function onLongWait(promise, time, action, reversal) {
  let done = false;
  let ran = false;

  /**
   * Give the promise {time} before it resolves
   * eg 200 milliseconds to prevent flickering when resolves are quick
   */
  wait(time).then(() => {
    if (done) return;
    ran = true;
    action();
  });

  promise.then((data) => {
    done = true;
    if (ran) reversal();
    return data;
  });

  return promise;
}

function dirty(el) {
  el.attr("data-dirty", true);
  el.closest(".input-container").attr("data-dirty", true);
}

function removeError(el) {
  const container = el.closest(".input-container");
  container.find(".error").addClass("hidden");
}
